/**
 * @lines98/client/screens/content-screen — контент-страница ВНУТРИ приложения.
 *
 * «Веточка из дерева» (CLAUDE.md «ствол → ветки»): экран — ТОНКАЯ ветка, которая
 * монтирует ОБЩИЙ контент-роутинг (`lib/content-routing`) в content-slot того же
 * глобального nav-rail, что и игровые экраны. Тот же контент, что краулер видит в
 * статике dist-content (источник — generated/content-bundle), та же оболочка/рейл,
 * один origin. Закрывает owner-критику «два разных сайта» (ADR-0020 §«Сшить
 * контент в приложение :5173»).
 *
 * Что рендерит: хлебные крошки (origin-relative) + lang-switcher (тот же slug на
 * другом языке) + тело статьи (готовый HTML из бандла) + CTA «Играть» (→ реальная
 * игра в этом же приложении, store-действие, не боевой домен). Внутренние ссылки в
 * теле (`/en/rules/` и т.п.) перехватываются делегированным кликом → SPA-переход
 * (pushState) без перезагрузки; «Играть»/корень → игра.
 *
 * Навигация: вход — рейл-пункт «Об игре» / лого / boot по пути `/en/…`; между
 * страницами — крошки/lang-switcher/ссылки в теле; «Играть» → игра. Все href
 * origin-relative (работают на :5173 и в проде).
 *
 * Layout/visual — styles/content.css (§5.0 layout-plan там). Статья СКРОЛЛИТСЯ по
 * вертикали (легитимно — не игровой viewport-locked экран; responsive-ui §1.2 искл.).
 *
 * Spec/skill: ADR-0020; responsive-ui §0/§5.0 (layout-plan в content.css); §0.B
 *   (labels/CTA must-visible). code-structure §1 (ствол→ветки, рейл = единый источник).
 */

import '../styles/content.css';
import '@lines98/shared/styles/help-board.css';

import { createScoringLadder, type ScoringLadderLang } from '../components/scoring-ladder.js';
import { mountHelpToc } from '../lib/help-toc.js';
import { attachTitleFit } from '../lib/title-fit.js';
import {
  contentNavLinks,
  navigateContent,
  navigateLandingSpa,
  resolveContentPage,
} from '../lib/content-routing.js';
import { applyGameRoute, classifyGamePath } from '../lib/game-routing.js';
import {
  canonicalEnRedirect,
  contentPath,
  parseContentPath,
} from '@lines98/shared/config/content-routes';
import { t } from '../lib/i18n/index.js';
import { useGameStore } from '../store/game-store.js';
import type { ScreenHandle } from './board-screen.js';

import { CONTENT_LANGS, DEFAULT_CONTENT_LANG, type UiLang } from '@lines98/shared/config/seo';

/**
 * Голый лендинг-путь под модель B = БЕЗ slug (`/` en-главная ИЛИ `/<lang>/`, напр.
 * `/ru/`): поверхность ЕДИНОЙ ГЛАВНОЙ (start-phase), НЕ статья. Возвращает язык если
 * путь — голый лендинг, иначе null (slug ≠ '' → контент-статья). Тот же критерий, что
 * в app.ts boot (fix/i18n-unify-homepage) — единый смысл «голого лендинга».
 */
function landingLangOf(pathname: string): UiLang | null {
  const parsed = parseContentPath(pathname);
  return parsed.slug === '' ? parsed.lang : null;
}

/**
 * Маршрутизировать origin-relative путь в SPA-переход: голый лендинг → СКРОЛЛ-ГЛАВНАЯ
 * (ЕДИНЫЙ ствол `navigateLandingSpa` из lib/content-routing — НЕ своя копия setLocale+
 * showStart), иначе контент-статья (navigateContent). Используется для канонизованных
 * `/en/...`-целей (голый `/en/` → главная; `/en/<slug>/` → статья). `replace` — для
 * канонизации (не плодить запись истории на `/en/`). navigateContent сам уводит на
 * главную при пустом slug (защитный пояс на фантом-лендинг), так что вызов с slug='' тоже
 * безопасен; здесь явно разделяем для ясного history-маркера landing.
 */
function goToTarget(pathname: string, replace: boolean): void {
  const land = landingLangOf(pathname);
  if (land !== null) {
    navigateLandingSpa(land, replace);
    return;
  }
  const parsed = parseContentPath(pathname);
  navigateContent(parsed.lang, parsed.slug, replace);
}

/** Канон-сигнатура таблицы очков по СТРУКТУРЕ (язык-независимо): col1 = длины линий
 * 5..9, col2 = очки 5,11,18,26,35 (формула n·(n+1)/2−10). Детект по этим числам, НЕ по
 * тексту заголовка — устойчиво на ВСЕХ 12 языках (owner-баг 2026-06-23: лестница должна
 * подменять excel-таблицу на КАЖДОМ языке, а не только ru/en). */
const SCORING_LINE_LENGTHS = [5, 6, 7, 8, 9];
const SCORING_POINTS = [5, 11, 18, 26, 35];

/**
 * Свести `lang` контент-роута к языку лестницы. CONTENT_LANGS поддержаны 1:1 в COPY
 * лестницы; неизвестный код (defensive) → en-фолбэк внутри createScoringLadder. Сравнение
 * по членству в COPY-неявно: приводим к ScoringLadderLang, если код валиден.
 */
function toLadderLang(lang: string): ScoringLadderLang {
  return (CONTENT_LANGS as readonly string[]).includes(lang)
    ? (lang as ScoringLadderLang)
    : (DEFAULT_CONTENT_LANG as ScoringLadderLang);
}

/** Распознать таблицу очков по СТРУКТУРЕ: 5 строк тела, col1=5..9, col2=5,11,18,26,35. */
function isScoringTable(table: HTMLTableElement): boolean {
  const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
  // marked кладёт строки данных в <tbody>; если его нет — берём все <tr> кроме шапки.
  const rows = bodyRows.length
    ? bodyRows
    : Array.from(table.querySelectorAll('tr')).filter((tr) => !tr.querySelector('th'));
  if (rows.length !== SCORING_LINE_LENGTHS.length) return false;
  return rows.every((tr, i) => {
    const cells = tr.querySelectorAll('td');
    if (cells.length < 2) return false;
    const a = parseInt((cells[0]?.textContent ?? '').replace(/\D/g, ''), 10);
    const b = parseInt((cells[1]?.textContent ?? '').replace(/\D/g, ''), 10);
    return a === SCORING_LINE_LENGTHS[i] && b === SCORING_POINTS[i];
  });
}

/**
 * ПРОГРЕССИВНОЕ УЛУЧШЕНИЕ (owner-direction 2026-06-21; расширено owner-баг 2026-06-23 на
 * ВСЕ 12 языков): найти таблицу(ы) очков в готовом bodyHtml и ЗАМЕНИТЬ их визуально на
 * «Лестницу очков» (components/scoring-ladder) — НА КАЖДОМ языке (раньше детект по ru/en-
 * заголовку → на de/es/ja/… лестница не появлялась, торчала голая excel-таблица).
 *
 * Детект — по СТРУКТУРЕ таблицы (`isScoringTable`: 5 строк, col1=5..9, col2=5,11,18,26,35),
 * не по тексту заголовка — язык-независимо и устойчиво (заголовки на 12 языках, CJK-регистр
 * и т.п. не ломают). Исходный markdown `<table>` НЕ удаляем (остаётся в статике dist-content
 * для SEO/no-JS/a11y; лестница несёт те же данные доступно через role=table + aria-label) —
 * в РАНТАЙМЕ SPA скрываем его (`hidden` → вне layout + a11y-дерева), лестницу вставляем
 * сразу после. Возвращает массив хэндлов для destroy на unmount.
 *
 * lang приходит из текущего content-роута экрана (resolveContentPage); лестница берёт
 * локальную i18n-мапу по нему (заголовки колонок на языке страницы). Не-CONTENT_LANGS →
 * en-фолбэк (toLadderLang).
 */
function enhanceScoringTables(root: HTMLElement, lang: string): { destroy(): void }[] {
  const ladderLang = toLadderLang(lang);
  const handles: { destroy(): void }[] = [];
  const tables = root.querySelectorAll<HTMLTableElement>('.content-body table');
  tables.forEach((table) => {
    if (!isScoringTable(table)) return; // не таблица очков — пропускаем
    // Видимые заголовки колонок берём из ТОЙ САМОЙ таблицы, которую заменяем
    // (единый источник = content/<lang>/scoring.md; никакой второй копии строк —
    // рассинхрон структурно невозможен, owner-баг 2026-06-23 / FE-review блокер-1).
    const ths = table.querySelectorAll('thead th');
    const lineHeader = (ths[0]?.textContent ?? '').trim();
    const pointsHeader = (ths[1]?.textContent ?? '').trim();
    const ladder = createScoringLadder({ lang: ladderLang, lineHeader, pointsHeader });
    // Исходную таблицу прячем из РАНТАЙМ-вёрстки и a11y (статика dist-content цела для SEO).
    table.hidden = true;
    table.setAttribute('aria-hidden', 'true');
    // Лестница встаёт на место таблицы (сразу после неё).
    table.insertAdjacentElement('afterend', ladder.element);
    handles.push(ladder);
  });
  return handles;
}

/** HTML-escape для атрибутных/текстовых позиций (бренд/подписи в SPA). */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Смонтировать контент-экран. Цель берётся из store `contentTarget` (выставлен
 * `showContent` / boot-роутингом). null-цель (defensive) → дефолт-лендинг.
 */
export function mountContentScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';

  const target = useGameStore.getState().contentTarget ?? { lang: '', slug: '' };
  const { lang, page, ui } = resolveContentPage(target.lang, target.slug);

  const nav = contentNavLinks(lang, page.slug);

  // Крошки: Home → текущая (для не-лендинга). Origin-relative href.
  const home = nav.find((n) => n.slug === '');
  const crumbs =
    page.slug === '' || !home
      ? [{ label: ui.homeLabel, slug: '', active: true }]
      : [
          { label: ui.homeLabel, slug: '', active: false },
          { label: page.navLabel, slug: page.slug, active: true },
        ];

  const root = document.createElement('article');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'content-screen screen-root';
  root.lang = lang;

  const crumbsHtml = crumbs
    .map((c, i) => {
      const last = i === crumbs.length - 1;
      return last
        ? `<span aria-current="page">${esc(c.label)}</span>`
        : `<a class="content-link" data-slug="${esc(c.slug)}" data-lang="${esc(lang)}" href="${esc(contentPath(lang, c.slug))}">${esc(c.label)}</a> <span aria-hidden="true">›</span> `;
    })
    .join('');

  // lang-switcher на самой странице УДАЛЁН (T-I18N-2 AC-13): функцию забрал глобальный
  // переключатель языка на рейле (singleton-контрол под логотипом). Краулерские
  // hreflang-<link> в <head> SSG-зеркала — НЕ здесь (для поисковика, не видимый switcher).

  // Раскладка (тикет start-page-redo-and-help §B; playtest-fixes AC-B6; §0.A P1/§1.1):
  // screen-root LOCKED (100dvh, overflow:hidden); ВНУТРИ — две зоны:
  //   - [.help-toc]  второй рейл-оглавление (lib/help-toc) — на ПК/широком sticky-
  //     КОЛОНКА слева, на touch/узком drawer (монтируется НИЖЕ, не в этой разметке);
  //   - [.content-main] статья: chrome (заголовок+крошки+язык, sticky сверху) + тело
  //     (.content-body — ЕДИНСТВЕННЫЙ скроллер, §1.2 искл.).
  // CTA «Играть» (AC-B6 playtest-fix): owner — НЕ sticky-футер (жрёт место). Стоит в
  // обычном потоке В КОНЦЕ .content-body (внутри скроллера статьи, как нижняя CTA
  // стартовой) → двигается с текстом при скролле, не прилипает к краю.
  // ОГЛАВЛЕНИЕ БОЛЬШЕ НЕ верхняя лента в chrome (AC-11, owner: закреплённые верхние
  // кнопки PROHIBITED) — оно живёт в колонке/drawer help-toc.
  root.innerHTML = `
    <div class="content-main">
      <header class="content-chrome">
        <div class="content-header">
          <div class="content-titleline postament-titleline">
            <div class="content-title postament-title screen-title">${esc(page.navLabel)}</div>
          </div>
        </div>
        <div class="content-topbar">
          <nav class="content-breadcrumbs" aria-label="Breadcrumb">${crumbsHtml}</nav>
        </div>
      </header>
      <div class="content-body">
        ${page.bodyHtml}
        <div class="content-foot">
          <button class="btn content-cta" type="button">${esc(ui.playCta)}</button>
        </div>
      </div>
    </div>
  `;

  host.appendChild(root);

  // ПРОГРЕССИВНОЕ УЛУЧШЕНИЕ: таблицу очков в теле статьи заменяем «Лестницей очков»
  // (components/scoring-ladder) — визуальный блок в стиле лидерборда. Исходная таблица
  // остаётся в bundle (SEO/no-JS/a11y), скрыта в рантайме; лестница встаёт на её место.
  const scoringLadders = enhanceScoringTables(root, lang);

  // Второй рейл-оглавление = ПЕРЕИСПОЛЬЗУЕМЫЙ МОДУЛЬ lib/help-toc (AC-11..16) — экран
  // только МОНТИРУЕТ его (колонка на ПК / drawer на touch), не верстает оглавление
  // руками. Список разделов внутри help-toc = lib/content-nav (единый источник).
  // Подпись переключателя — «Разделы Помощи» (отличается от глобального «Меню» рейла,
  // AC-16: переключатель Help — отдельная кнопка с собственным aria-label, не дубль
  // навигации продукта). Лейбл оглавления-списка = ui.menuLabel (i18n).
  const tocToggleLabel = t('game.content.tocAria');
  const helpToc = mountHelpToc(root, {
    lang,
    activeSlug: page.slug,
    navLabel: ui.menuLabel,
    toggleLabel: tocToggleLabel,
  });

  // --- Делегированный перехват внутренних ссылок ---------------------------------
  // Тело статьи содержит реальные `<a href="/en/rules/">` (краулер видит их в
  // статике); крошки/lang-switcher — `.content-link` с data-атрибутами. На клик
  // внутри приложения перехватываем оба и делаем SPA-переход (pushState) вместо
  // полной перезагрузки. Внешние ссылки и не-контентные пути — пропускаем браузеру.
  const onClick = (e: MouseEvent): void => {
    // Модификаторы (новая вкладка) / не-левый клик — отдаём браузеру.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    const a = (e.target as HTMLElement | null)?.closest('a');
    if (!a) return;

    // Крошки/lang-switcher: явные data-атрибуты (href="#" — служебный).
    if (a.classList.contains('content-link')) {
      e.preventDefault();
      const dl = a.getAttribute('data-lang') || lang;
      const ds = a.getAttribute('data-slug') ?? '';
      navigateContent(dl, ds);
      return;
    }

    // Ссылки в теле статьи (URL-МОДЕЛЬ B, ADR-0021 addendum-001): порядок разбора
    // как в boot — `/en/`-канон → ГОЛЫЙ ЛЕНДИНГ (`/`,`/ru/`) → ИГРОВОЙ путь → контент-
    // статья; реплей/oauth/внешнее → браузеру. Под моделью B контентный парсер матчит
    // всё, поэтому лендинг/игровые проверяем ДО него.
    const href = a.getAttribute('href');
    if (!href || href.startsWith('http') || a.target === '_blank') return;
    let url: URL;
    try {
      url = new URL(href, window.location.origin);
    } catch {
      return;
    }
    if (url.origin !== window.location.origin) return; // внешняя — браузеру
    const p = url.pathname;
    // `/en/...` → канонизировать на корне-вариант (модель B: en только в корне). Голый
    // `/en/` → главная (start); `/en/<slug>/` → статья.
    const enCanon = canonicalEnRedirect(p);
    if (enCanon !== null) {
      e.preventDefault();
      goToTarget(enCanon, /* replace */ true);
      return;
    }
    // ГОЛЫЙ ЛЕНДИНГ (`/` en-главная ИЛИ `/<lang>/` без slug) → ЕДИНАЯ ИНДЕКСИРУЕМАЯ
    // ГЛАВНАЯ (start-phase), НЕ игра (fix/i18n-unify-homepage: `/` = лендинг, AC-5;
    // прежний рассогласованный `applyGameRoute('play')` СНЯТ). Язык URL → главная следует.
    const land = landingLangOf(p);
    if (land !== null) {
      e.preventDefault();
      navigateLandingSpa(land); // ЕДИНЫЙ ствол главной (lib), не своя копия
      return;
    }
    // Игровой путь (`/play`, `/daily`, …) → соответствующая фаза.
    const game = classifyGamePath(p);
    if (game) {
      e.preventDefault();
      applyGameRoute(game);
      return;
    }
    // Реплей `/r/<id>` / oauth — отдаём браузеру (full nav, server-authoritative).
    if (/^\/r\/[0-9A-Za-z]{8,32}\/?$/.test(p) || p.startsWith('/oauth/')) return;
    // Иначе — контентная СТАТЬЯ (модель B: en `/rules/`, ru `/ru/faq/`, заглушка).
    const parsed = parseContentPath(p);
    e.preventDefault();
    navigateContent(parsed.lang, parsed.slug);
  };
  root.addEventListener('click', onClick);

  // CTA «Играть» → ИГРОВОЙ per-route через ЕДИНЫЙ ствол lib/game-routing (AC-19/20):
  // applyGameRoute('play') меняет фазу → app.ts автоматически синхронизирует URL на
  // игровой путь (pathForPhase). Целевой путь НЕ хардкодится строкой в экране —
  // резолвится game-routing'ом (параметризуемо под owner-decision о корне `/`, AC-20).
  const cta = root.querySelector<HTMLButtonElement>('.content-cta');
  cta?.addEventListener('click', () => applyGameRoute('play'));

  // Скролл наверх при входе на новую страницу (между статьями — свежий первый экран).
  // Скроллится .content-body (текст), не screen-root (chrome остаётся в кадре, §1.1).
  root.querySelector<HTMLElement>('.content-body')!.scrollTop = 0;

  // Заголовок экрана — ЕДИНЫЙ источник вида для ВСЕХ экранов (postament.css §screen-title,
  // owner-directive 2026-05-30): `.screen-title` + attachTitleFit (line-autofit, capToDesign).
  // Контент теперь показывает заголовок раздела так же, как board/daily/result — не кустарно.
  const titleFit = attachTitleFit(root);

  return {
    unmount() {
      titleFit.detach();
      helpToc.detach();
      scoringLadders.forEach((l) => l.destroy());
      root.removeEventListener('click', onClick);
      host.innerHTML = '';
    },
  };
}
